import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { beatPulse } from '../utils/worldBeat';
import { api } from '../services/api';
import { audioEngine } from '../services/audioEngine';
import { API_BASE, IS_MOBILE } from '../config';
import { freeGltf } from '../utils/gltfMemory';

// Sign "families" we drive with dynamic graphics. Identified with ?pick=1.
// A base name matches that mesh AND all its instances across the city
// (`<base>`, `<base>_2`, `<base>_3`, …), so one entry lights up every copy.
// Tall panels (portrait) suit HypeClips / album art. Add more as we pick them.
const SIGN_MESHES = ['pasted__pCube88_Pawnshop1GLOW_0'];
const signBaseFor = (name) => name && SIGN_MESHES.find((b) => name === b || name.startsWith(b + '_'));

// WebGL textures need CORS headers; GCS buckets don't send them. Stream the
// image through the API's /api/assets/file proxy (which carries cors()) so the
// texture loads cross-origin. Passes non-GCS URLs through untouched.
function proxied(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'storage.googleapis.com') {
      const parts = u.pathname.replace(/^\//, '').split('/');
      const bucket = parts.shift();
      const name = parts.map(decodeURIComponent).join('/');
      return `${API_BASE}/api/assets/file?bucket=${encodeURIComponent(bucket)}&name=${encodeURIComponent(name)}`;
    }
  } catch { /* fall through */ }
  return url;
}

// Give a sign mesh its own material, remapping its UVs to fill the panel (they
// map into a shared atlas region otherwise) and cloning the geometry so we
// don't disturb other meshes sharing it. The tall (portrait) signs play a
// HypeClip — a looping muted 9:16 video that fits the panel; clicking unmutes
// it. Returns the created <video> (or null) for cleanup + click-to-play.
function paintSign(mesh, { videoUrl, imageUrl }) {
  if (!mesh?.geometry) return null;
  const g = mesh.geometry.clone();
  const uv = g.attributes.uv;
  if (uv) {
    let minU = Infinity, minV = Infinity, maxU = -Infinity, maxV = -Infinity;
    for (let i = 0; i < uv.count; i++) {
      const u = uv.getX(i), v = uv.getY(i);
      minU = Math.min(minU, u); maxU = Math.max(maxU, u);
      minV = Math.min(minV, v); maxV = Math.max(maxV, v);
    }
    const du = (maxU - minU) || 1, dv = (maxV - minV) || 1;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (uv.getX(i) - minU) / du, (uv.getY(i) - minV) / dv);
    uv.needsUpdate = true;
  }
  mesh.geometry = g;

  let tex, video = null;
  if (videoUrl) {
    video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = proxied(videoUrl);
    video.loop = true; video.muted = true; video.playsInline = true; video.preload = 'auto';
    video.play().catch(() => {});
    tex = new THREE.VideoTexture(video);
  } else {
    tex = new THREE.TextureLoader().load(
      proxied(imageUrl),
      (t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 16; t.needsUpdate = true; },
      undefined,
      () => console.warn('[sign] media failed to load:', imageUrl),
    );
  }
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 16;          // sharp at the steep viewing angles of a wall sign
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = videoUrl ? THREE.LinearFilter : THREE.LinearMipmapLinearFilter;
  // DoubleSide: imported sign faces often point inward, which FrontSide culls
  // (the sign would vanish). Unlit so it reads like a lit billboard.
  mesh.material = new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide, toneMapped: false });
  return video;
}

// CityModel — a pre-built stylized city (GLTF) as the walkable environment, so
// the whole streetscape is real modeled geometry, not textured boxes. The
// model ships at a huge authoring scale, so we measure its true world bounds
// after load and fit + ground it. Its own GLOW materials give night windows.
//
// It also carries a parked car (a Lotus "elise" under `pasted__group1`); we
// report its world position so the fly-in can land the walker right beside it.
//
// Preview mode: ?city=model (keeps OSM + the hand-built town intact).

const MODEL_URL = '/models/ccity_building_set_1.glb';
const FIT_METRES = 240; // fit the city's larger horizontal span to this

// Venues anchor to real named buildings, chosen to be SPREAD ACROSS the city
// (so travelling between them is a real journey). The concert hall seeds on
// the Paramount theatre; the other three are farthest-point-sampled from the
// candidate landmarks so they land far apart.
const CONCERT_SEED = 'LM_Paramount1';
const ANCHOR_CANDIDATES = [
  'LM_Paramount1', 'LM_Paramount7', 'LM_Paramount9', 'LM_Liquor', 'LM_Clinic2',
  'LM_Laundrette', 'LM_PawnShop1', 'LM_PawnShop2', 'LM_PawnShop3',
  'Filler_GunShop', 'Filler_CityArms2', 'Filler_Liquor1', 'Filler_Housing',
  'Filler_Apartments2', 'Filler_Housing1', 'Filler_Housing2', 'Filler_GunShop1',
];
const VENUE_ORDER = ['concert-hall', 'studio', 'listening-room', 'record-store', 'theater'];
const ANCHOR_FALLBACK = {
  'concert-hall': [55, 18, 40], studio: [-55, 18, -35],
  'listening-room': [45, 18, -50], 'record-store': [-45, 18, 55],
  theater: [0, 18, -55],
};

export default function CityModel({ night = 0 }) {
  const { scene } = useGLTF(MODEL_URL);
  const model = useMemo(() => scene.clone(true), [scene]);
  // Mobile: when the city unmounts (entering an interior), DUMP its GPU memory so
  // a Gaussian splat has room to load without OOM-crashing iOS. It reloads (from
  // HTTP cache) on return to the street. Desktop keeps it cached (dispose={null}).
  useEffect(() => {
    if (!IS_MOBILE) return undefined;
    return () => freeGltf(model, MODEL_URL);
  }, [model]);
  const nightRef = useRef(night);
  nightRef.current = night; // 0 (day) → 1 (night): the beat pulse fades in with dusk
  const setLandmark = useWorldStore((s) => s.setLandmark);
  const setVenueAnchors = useWorldStore((s) => s.setVenueAnchors);
  const setVenueBoxes = useWorldStore((s) => s.setVenueBoxes);
  const setPicked = useWorldStore((s) => s.setPicked);
  const PICK = useMemo(() => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('pick') === '1', []);

  // ?pick=1: click any mesh to identify it (name, material, size) so we can
  // target a specific sign for dynamic graphics.
  const onPick = (e) => {
    e.stopPropagation();
    const o = e.object;
    const b = new THREE.Box3().setFromObject(o);
    const s = new THREE.Vector3(); b.getSize(s);
    const r = (v) => Math.round(v * 10) / 10;
    setPicked({ name: o.name || '(unnamed)', material: o.material?.name || '(none)', size: [r(s.x), r(s.y), r(s.z)] });
  };

  // HypeClips → played on the tall (portrait) city signs. Each sign loops a
  // muted 9:16 clip; clicking it unmutes that clip (single-source audio).
  const [clips, setClips] = useState([]);
  const signMap = useRef({});
  useEffect(() => {
    api.hypeClips().then((d) => setClips((d?.clips || []).filter((c) => c.gcs_url))).catch(() => setClips([]));
  }, []);
  useEffect(() => {
    if (!clips.length) return undefined;
    const map = {};
    const videos = [];
    let i = 0;
    model.traverse((o) => {
      if (!o.isMesh || !signBaseFor(o.name)) return;
      const clip = clips[i % clips.length];
      i += 1;
      const v = paintSign(o, { videoUrl: clip.gcs_url });
      if (v) { videos.push(v); map[o.name] = { clip, video: v }; }
    });
    signMap.current = map;
    return () => { videos.forEach((v) => { try { v.pause(); v.removeAttribute('src'); v.load(); } catch { /* noop */ } }); };
  }, [model, clips]);

  const onClickModel = (e) => {
    // A wired sign always plays (even in ?pick=1 mode); otherwise pick logs it.
    const entry = signMap.current[e.object?.name];
    if (entry?.video) { e.stopPropagation(); audioEngine.playVideo(entry.video); return; }
    if (PICK) onPick(e);
  };

  // Beat pulse — the city's own GLOW lights (its emissive materials: windows,
  // signage, street/traffic lamps baked into the model) breathe with the song,
  // like the old world's lights. Collect the emissive materials once (deduped;
  // the dynamic video signs are excluded — they carry their own footage), cache
  // each one's base intensity, then swell them a single gentle hump per beat.
  const glowMats = useRef([]);
  useEffect(() => {
    const seen = new Map(); // material.uuid → { mat, base }
    model.traverse((o) => {
      if (!o.isMesh || signBaseFor(o.name)) return;
      const list = Array.isArray(o.material) ? o.material : [o.material];
      list.forEach((m) => {
        if (!m || seen.has(m.uuid)) return;
        const base = m.emissiveIntensity ?? 1;
        const lit = m.emissive && base > 0.01 && (m.emissive.r + m.emissive.g + m.emissive.b > 0.05 || m.emissiveMap);
        if (lit) seen.set(m.uuid, { mat: m, base });
      });
    });
    glowMats.current = [...seen.values()];
    return () => { glowMats.current.forEach(({ mat, base }) => { mat.emissiveIntensity = base; }); glowMats.current = []; };
  }, [model]);

  // BPM detection often lands an OCTAVE off (e.g. 184 for a 92-BPM song), which
  // reads as "faster than the tempo." Fold the analyzed bpm into a normal
  // tapping range so octave-errors self-correct and every song pulses near its
  // felt tempo — without breaking songs whose bpm is already right.
  //
  // Anchor the phase to the SONG PLAYHEAD (audioEngine.broadcastTime), not the
  // free-running render clock: it resets every track, so a slightly-off bpm
  // can't accumulate drift across a set (the "sways on and off beat" problem),
  // and peaks land on the song's own grid. Falls back to the render clock only
  // when the broadcast isn't the source (ambient pulse layer).
  useFrame(() => {
    // Pulse is a NIGHT feature: by day the lights sit steady at base (k=1); as
    // dusk falls (night → 1) the beat throb fades in (0.5×…1.8× base).
    const k = 1 + nightRef.current * (beatPulse() * 1.3 - 0.5);
    const list = glowMats.current;
    for (let i = 0; i < list.length; i++) list[i].mat.emissiveIntensity = list[i].base * k;
  });

  const { scale, position } = useMemo(() => {
    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3(); box.getSize(size);
    const center = new THREE.Vector3(); box.getCenter(center);
    const maxXZ = Math.max(size.x, size.z) || 1;
    const s = FIT_METRES / maxXZ;
    // Centre on the origin and drop the model's bottom to y=0.
    return { scale: s, position: [-center.x * s, -box.min.y * s, -center.z * s] };
  }, [model]);

  // Report the parked car's world position (after the fit transform) so the
  // model fly-in can land beside it.
  useEffect(() => {
    let car = model.getObjectByName('pasted__group1');
    if (!car) model.traverse((o) => { if (!car && /elise|steeringwheel|_tire_/i.test(o.name || '')) car = o; });
    if (!car) return;

    // The baked hero Lotus is authored oversized vs the town's ~1.8m residents.
    // Shrink the car NODE in place, anchoring its base-centre in world space so it
    // stays parked on the same spot and grounded (no float / no drift). Tunable
    // live with ?herocar=<fraction> (0.6 = 60% of the baked size).
    const HERO_CAR_SCALE = (() => {
      const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get('herocar'));
      return Number.isFinite(v) && v > 0 ? v : 0.7;
    })();
    if (HERO_CAR_SCALE !== 1 && car.parent && !car.userData._heroScaled) {
      car.userData._heroScaled = true; // idempotent: never compound (StrictMode / re-runs)
      car.updateWorldMatrix(true, true);
      const b0 = new THREE.Box3().setFromObject(car);
      const c0 = b0.getCenter(new THREE.Vector3());
      const anchor0 = new THREE.Vector3(c0.x, b0.min.y, c0.z); // world base-centre before
      car.scale.multiplyScalar(HERO_CAR_SCALE);
      car.updateWorldMatrix(true, true);
      const b1 = new THREE.Box3().setFromObject(car);
      const c1 = b1.getCenter(new THREE.Vector3());
      const anchor1 = new THREE.Vector3(c1.x, b1.min.y, c1.z); // world base-centre after
      // Move the anchor back to where it was, converting the world delta into the
      // parent's local space (correct under the parent's fit scale/offset).
      const inv = new THREE.Matrix4().copy(car.parent.matrixWorld).invert();
      const localBack = anchor0.clone().applyMatrix4(inv).sub(anchor1.clone().applyMatrix4(inv));
      car.position.add(localBack);
      car.updateWorldMatrix(true, true);
    }

    const p = new THREE.Vector3();
    car.getWorldPosition(p); // walks parents incl. this model's scale/position
    setLandmark([p.x, p.y, p.z]);

    // Measure each candidate building: XZ centre + roof height (for the sign).
    const cand = [];
    for (const name of ANCHOR_CANDIDATES) {
      const node = model.getObjectByName(name);
      if (!node) continue;
      const box = new THREE.Box3().setFromObject(node);
      if (!isFinite(box.min.x)) continue;
      const c = new THREE.Vector3(); box.getCenter(c);
      const s = new THREE.Vector3(); box.getSize(s);
      cand.push({ name, x: c.x, z: c.z, top: box.max.y, center: [c.x, c.y, c.z], size: [s.x, s.y, s.z] });
    }
    const dist2 = (a, b) => (a.x - b.x) ** 2 + (a.z - b.z) ** 2;
    let picks = [];
    const seed = cand.find((c) => c.name === CONCERT_SEED) || cand[0];
    if (seed) {
      picks = [seed];
      while (picks.length < 5 && picks.length < cand.length) {
        let best = null, bestD = -1;
        for (const c of cand) {
          if (picks.includes(c)) continue;
          const d = Math.min(...picks.map((p) => dist2(p, c)));
          if (d > bestD) { bestD = d; best = c; }
        }
        if (!best) break;
        picks.push(best);
      }
    }
    const anchors = {};
    const boxes = {};
    VENUE_ORDER.forEach((key, i) => {
      const p = picks[i];
      anchors[key] = p ? [p.x, p.top, p.z] : ANCHOR_FALLBACK[key];
      if (p) boxes[key] = { center: p.center, size: p.size };
    });
    setVenueAnchors(anchors);
    setVenueBoxes(boxes);

    return () => { setLandmark(null); setVenueAnchors(null); setVenueBoxes(null); };
  }, [model, scale, position, setLandmark, setVenueAnchors, setVenueBoxes]);

  // dispose={null}: this is a clone(true) of useGLTF's CACHED scene, so it SHARES
  // geometries/materials with the cache. Entering an interior unmounts the street
  // branch; without this, r3f would dispose those shared resources on unmount and
  // buildings (e.g. the studio) would come back invisible after stepping out.
  return <primitive object={model} scale={scale} position={position} onClick={onClickModel} dispose={null} />;
}

useGLTF.preload(MODEL_URL);
